'use client';
import React from 'react';
import { AutocompleteModule, ActionModule, SearchModule, TagModule, ClipboardModule, QueryScore } from './Modules';
import { noop } from '@benzinga/utils';
import { TrendingTopicsManager } from '@benzinga/trending-topics-manager';
import { SessionContext } from '@benzinga/session-context';
import { ResultDescription, ResultHead } from '../Expression/ExpressionSearch';
import {
  KeywordModuleId,
  KeywordSearchItem,
  KeywordSearchItemData,
  KeywordSearchItemVersion,
  SearchItem,
  createKeywordSearchItem,
  isKeywordSearchItem,
  isLegacyKeywordSearchItem,
} from '@benzinga/search-modules';
import { SearchContext, SearchContextType } from '../context';
import Styles from '../styles/styles.module.scss';

export interface KeywordSearchModule extends SearchModule<KeywordSearchItemData> {
  id: typeof KeywordModuleId;
  autocomplete: AutocompleteModule<KeywordSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

const KeywordDropdown: React.FC<{ isSelected: boolean; isLocked?: boolean; item: KeywordSearchItem }> = props => (
  <>
    <ResultHead $isSelected={props.isSelected} className={Styles.resultHead}>
      {props.item.data.isTrending ? 'Trending Keyword' : 'Keyword'}
    </ResultHead>
    <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
      {props.item.data.keyword}
    </ResultDescription>
  </>
);

export const createKeywordAutocompleteModule = (
  trendingTopicsManager: TrendingTopicsManager,
  searchContext: SearchContextType,
): AutocompleteModule<KeywordSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isKeywordSearchItem(item)) return undefined;
      return {
        component: ({ isLocked, isSelected }) => (
          <KeywordDropdown isLocked={isLocked} isSelected={isSelected} item={item} />
        ),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: KeywordSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isKeywordSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      const moduleSelectedItems = selectedItems?.flatMap(item => (isKeywordSearchItem(item) ? [item] : [])) ?? [];

      return {
        cancel: noop,
        promise: new Promise(resolve => {
          (async () => {
            if (text === '') {
              const trendingTopics = await trendingTopicsManager
                .getTrendingTopics(['entities', 'channels'])
                .then(topics => (topics.ok ? topics.ok : []));
              return resolve({
                canceled: false,
                ok: [
                  ...trendingTopics.map<[SearchItem<KeywordSearchItemData>, QueryScore]>(topic => {
                    return [createKeywordSearchItem(topic.name, true), QueryScore.Priority];
                  }),
                  ...searchContext
                    .getRecentModuleItem(undefined)
                    .filter(isKeywordSearchItem)
                    .filter(
                      item =>
                        !moduleSelectedItems.some(selectedItem => selectedItem.data.keyword === item.data.keyword),
                    )
                    .map<[SearchItem<KeywordSearchItemData>, QueryScore]>(item => [item, QueryScore.HistoryMatch]),
                ]
                  .filter(
                    (item, idx1, allItems) =>
                      moduleSelectedItems.every(selectedItem => selectedItem.data.keyword !== item[0].data.keyword) &&
                      allItems.every(
                        (selectedItem, idx2) => idx2 >= idx1 || selectedItem[0].data.keyword !== item[0].data.keyword,
                      ),
                  )
                  .slice(0, limit),
              });
            } else
              return resolve({
                canceled: false,
                ok: [text]
                  .map<
                    [SearchItem<KeywordSearchItemData>, QueryScore]
                  >(t => [createKeywordSearchItem(t), QueryScore.FullMatch])
                  .filter(
                    item =>
                      !moduleSelectedItems.some(selectedItem => selectedItem.data.keyword === item[0].data.keyword),
                  ),
              });
          })();
        }),
      };
    },
  };
};

export const createKeywordTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isKeywordSearchItem(item)) return undefined;
      return {
        text: item.data.keyword,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isKeywordSearchItem(item)) return undefined;
      return item.data.keyword;
    },
  };
};

export const createKeywordActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createKeywordClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isKeywordSearchItem(token)) return undefined;
      return [token];
    },
    onPasteJSON: (token): KeywordSearchItem | undefined => {
      if (isLegacyKeywordSearchItem(token)) return KeywordModuleItemLoader(token);
      return undefined;
    },
  };
};

const KeywordModuleItemLoader = (item: SearchItem) => {
  if (!isLegacyKeywordSearchItem(item)) return undefined;

  switch (item.version) {
    case KeywordSearchItemVersion:
    default:
      return item;
  }
};

export const createKeywordSearchModule = (
  trendingTopicsManager: TrendingTopicsManager,
  searchContext: SearchContextType,
): KeywordSearchModule => {
  return {
    action: createKeywordActionModule(),
    autocomplete: createKeywordAutocompleteModule(trendingTopicsManager, searchContext),
    clipboard: createKeywordClipboardModule(),
    id: KeywordModuleId,
    loader: KeywordModuleItemLoader,
    tag: createKeywordTagModule(),
  };
};

export const useKeywordSearchModule = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const trendingTopicsManager = session.getManager(TrendingTopicsManager);
  return React.useMemo(() => createKeywordSearchModule(trendingTopicsManager, search), [trendingTopicsManager, search]);
};
